import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-semantic-toasts';
import {
  Button,
  Checkbox,
  Dimmer,
  Input,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  TextArea
} from 'semantic-ui-react';
import Can from '../../../abilities/Can';
import IconButton from "../../../components/shared/buttons/IconButton";
import { history } from "../../../routers/AppRouters";
import { startGetCutoffPM } from '../../../store/actions/core/administration/cutoff';
import {
  clearPfe,
  setFinancialDataModalOpen,
  startGetPfe,
  startSavePfe
} from '../../../store/actions/shared/professionalFigures';
import constants from '../../../utils/constants';
import { areThereDuplicateFigures } from '../../../utils/projectPlanningUtils';
import { sanitizeNoteText, validateYubikCode } from '../../../utils/regexUtils';
import { capitalize } from '../../../utils/stringUtils';
import PfeNextEditProductContainer from './PfeNextEditProductContainer';
import PfeNextEditServiceContainer from './PfeNextEditServiceContainer';

const FIELDS = {
  project_code: 'Project Code',
  currency_code: 'Currency',
  start_date: 'Start Date',
  end_date: 'End Date',
  /* star_end_date: 'Project Duration', */
  status: 'Status',
  project_type: 'Project Type',
  offering_type: 'Offering Type',
  approval_date: 'Approval Date',
};

const MAX_NOTE_LENGTH = 500;

function PfeNextEditContainer(props) {
  const pfe_id = props.match.params.pfe_id
  const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmNote, setConfirmNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [existingYubikCodes, setExistingYubikCodes] = useState([]);
  const [newYubikCodes, setNewYubikCodes] = useState([]);
  const [currentYubikCode, setCurrentYubikCode] = useState('');
  const [yubikCodeError, setYubikCodeError] = useState('');
  const [isYubikNotAvailable, setIsYubikNotAvailable] = useState(false);
  const [yubikValidationError, setYubikValidationError] = useState('');

  useEffect(() => {
    props.getPfe(pfe_id)
    props.getCutoffPM()

    return () => {
      clearPfe()
    }
  }, [])

  const isFinancialDataComplete = (financialData, pfeDetails) => {
    const {
      revenues,
      billable_expenses,
      expenses,
      contingency_percentage,
      currency_risk_percentage,
    } = financialData

    const variables = [revenues];
    // AEP-4755: Modifichiamo il controllo per permettere le revenues a 0
    const hasNullValue = variables.some(variable => variable === null || variable === undefined);
    // bugfix 107
    if (contingency_percentage < 0) return true
    if (currency_risk_percentage < 0) return true

    if (hasNullValue) {
      return true;
    }
    return false
  }

  const invalidFinancialData = (financialData, pfeDetails) => {
    const {
      revenues,
      billable_expenses,
      cost,
      expenses,
      contingency_percentage,
      currency_risk_percentage,
      sg_percentage,
    } = financialData

    if (pfeDetails !== undefined && pfeDetails.revenues_actual !== undefined && revenues < pfeDetails.revenues_actual) return "Revenues"
    if (pfeDetails !== undefined && pfeDetails.billable_expenses_actual !== undefined && billable_expenses < pfeDetails.billable_expenses_actual) return "Billable Expenses"
    if (pfeDetails !== undefined && pfeDetails.expenses_actual !== undefined && expenses < pfeDetails.expenses_actual) return "Expenses"
    if (pfeDetails !== undefined && pfeDetails.costs_actual !== undefined && cost < pfeDetails.costs_actual) return "Costs" //TODO error message for user

    return ''
  }

  const areProfessionaFiguresComplete = (figures) => {
    const incompleteFigures = figures.filter(x => x.company === "" || x.level === "" || x.area === "" || x.sales_level === "" || (x.level === "EXT" && x.hourly_cost === 0))
    return incompleteFigures.length === 0
  }

  const addYubikCode = () => {
    const trimmedCode = currentYubikCode.trim();

    if (!trimmedCode) return;

    if (!validateYubikCode(trimmedCode)) {
      setYubikCodeError('Invalid format. Expected: T_CCC_YYYY_AAAA or T_CCC_YYYY_AAAA_VVV (e.g., O_ITA_2025_A001)');
      return;
    }

    // Controllo duplicati su entrambi gli array (esistenti + nuovi)
    // per prevenire reinserimento di codici già presenti
    if (existingYubikCodes.includes(trimmedCode) || newYubikCodes.includes(trimmedCode)) {
      setYubikCodeError('This code has already been added');
      return;
    }

    setNewYubikCodes([...newYubikCodes, trimmedCode]);
    setCurrentYubikCode('');
    setYubikCodeError('');
  };

  const removeYubikCode = (codeToRemove) => {
    // Rimuovi solo dai nuovi codici, non da quelli esistenti
    setNewYubikCodes(newYubikCodes.filter(code => code !== codeToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addYubikCode();
    }
  };

  const handleNoteChange = (e) => {
    const value = e.target.value;

    // Controllo solo la lunghezza, NON sanitizzo durante la digitazione
    // La sanitizzazione avverrà solo al momento del salvataggio
    if (value.length <= MAX_NOTE_LENGTH) {
      setConfirmNote(value);
      setNoteError(''); // Reset errore quando l'utente modifica
    }
  };

  const handleNotePaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const sanitizedPastedText = sanitizeNoteText(pastedText);
    const currentLength = confirmNote.length;
    const selectionStart = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;

    // Calcola la lunghezza finale dopo l'incolla con testo sanitizzato
    const newLength = currentLength - (selectionEnd - selectionStart) + sanitizedPastedText.length;

    if (newLength > MAX_NOTE_LENGTH) {
      e.preventDefault();
      setNoteError(`Cannot paste: the text would exceed the maximum limit of ${MAX_NOTE_LENGTH} characters (${newLength} characters total)`);
    }
  };

  const handleCloseModal = () => {
    const hasUnsavedData = confirmNote.trim() !== '' || newYubikCodes.length > 0 || isYubikNotAvailable;

    if (hasUnsavedData) {
      const confirmed = window.confirm('Are you sure? You will lose all unsaved changes.');

      if (!confirmed) {
        return;
      }
    }
    setIsConfirmModalOpen(false);
  };

  const openConfirmModal = () => {
    // Reset modal state e inizializza i codici esistenti dal PFE
    setConfirmNote('');
    setNoteError('');
    setExistingYubikCodes(props.pfe.yubik_offer_codes || []);
    setNewYubikCodes([]);
    setCurrentYubikCode('');
    setYubikCodeError('');
    setIsYubikNotAvailable(false);
    setYubikValidationError('');
    setIsConfirmModalOpen(true);
  };

  const onSave = (isConfirm, note = '', yubikCodes = []) => {

    const onFailure = res => {
      var description = 'Error saving PFE';
      if (res.response !== undefined && (res.response.status === 406 || res.response.status === 403)) {
        description = res.response.data.error
      }
      toast({
        title: 'Error',
        description: description,
        type: 'error',
        icon: 'file',
        time: constants.TOAST_ERROR_TIMEOUT,
        animation: 'fly left',
      });
    };

    const onSuccess = res => {
      toast({
        title: 'Success',
        description: 'The PFE has been saved',
        icon: 'check',
        type: 'success',
        time: constants.TOAST_SUCCESS_TIMEOUT,
        animation: 'fly left',
      });
    }

    // Sanitizza la nota SOLO al momento del salvataggio
    const sanitizedNote = sanitizeNoteText(note);

    let body = null;
    if (props.pfe.type === "PROD") {
      body = {
        status: "confirmed",
        pfe_summary: props.costsAndRevenues
      }

      // Aggiungi i nuovi campi solo in fase di conferma
      if (isConfirm) {
        body.note = sanitizedNote;
        // Invia SOLO i nuovi codici YUBIK, non quelli storici/esistenti
        body.yubik_offer_codes = yubikCodes;
      }
    } else {
      if (isFinancialDataComplete(props.financialData)) {
        // console.log('isFinancialDataComplete - financialData', props.financialData);

        toast({
          title: 'Warning',
          description: 'Some data is missing in the Financial Data section. Please complete this section.',
          type: 'warning',
          icon: 'file',
          time: constants.TOAST_WARNING_TIMEOUT,
          animation: 'fly left',
        });
        return;
      }

      if (isConfirm) {
        const invalidFinancialDataDescription = invalidFinancialData(props.financialData, props.pfeDetails)
        if (invalidFinancialDataDescription !== "") {
          toast({
            title: 'Warning',
            description: `${invalidFinancialDataDescription} cannot be lower than actual`,
            type: 'warning',
            icon: 'file',
            time: constants.TOAST_WARNING_TIMEOUT,
            animation: 'fly left',
          });
          return;
        }
      }

      if (areThereDuplicateFigures(props.professionalFigures, "pfe")) {
        toast({
          title: 'Warning',
          description: 'There are some professional figures with the same company, area, internal level and sales level. Please check and remove duplicates.',
          type: 'warning',
          icon: 'file',
          time: constants.TOAST_WARNING_TIMEOUT,
          animation: 'fly left',
        });
        return;
      }

      body = {
        status: isConfirm ? 'confirmed' : 'draft',
        pfe_details: props.professionalFigures.map(figure => (
          figure.hours.map(h => ({
            ...figure,
            year: h.year,
            month: h.month,
            hours: h.hour,
            internal: !figure.external,
            sale_level: figure.sales_level,
            fromDB: undefined,
            cost_hour: figure.hourly_cost,
            hourly_cost: undefined,
            company_id: undefined,
            site_percentage_expense: null,
            cost_hour_expense: null,
            //BUG FIX UAT 52: mandare hourly_rate reale invece che 0, perche' 0 spacca lo storico del DB
            hourly_rate: figure.real_hourly_rate,
            real_hourly_rate: undefined
          }))
        )).flat(),
        pfe_summary: props.financialData
      }

      // Aggiungi i nuovi campi solo in fase di conferma
      if (isConfirm) {
        body.note = sanitizedNote;
        // Invia SOLO i nuovi codici YUBIK, non quelli storici/esistenti
        body.yubik_offer_codes = yubikCodes;
      }
    }
    props.startSavePfe(pfe_id, body, onFailure, onSuccess)
    history.push('/project-planning/PFE_next')
  }

  const isButtonDisabled = () => {
    if (!(props.pfe.status === "created" || props.pfe.status === "rejected" || props.pfe.status === "draft"))
      return true
    if (!areProfessionaFiguresComplete(props.professionalFigures)) {
      return true
    }
    return false;
  }

  useEffect(() => {
    const listener = (event) => {
      event.preventDefault();
      return "You haven't saved or confirmed the PFE, do you want to save the PFE? Otherwise some data may be lost."
    }
    window.addEventListener("beforeunload", listener);

    return () => {
      window.removeEventListener("beforeunload", listener)
    }
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 50px)' }}>
      <Dimmer active={props.loading} inverted>
        <Loader indeterminate inverted content="Loading" size="medium" />
      </Dimmer>

      <Can I="project_planning:Read" a="pfes_next">
        {props.pfe !== undefined &&
          <>
            <Segment style={{ height: 'calc(100vh - 50px)', maxWidth: '200px', display: 'flex', flexDirection: 'column', margin: "0" }} floated='left'>
              <h2>PFE</h2>
              {Object.keys(FIELDS).map(key => (
                <div className="custom-form-field" style={{ margin: '7px' }} key={key}>
                  <label>{FIELDS[key]}</label>
                  {key === "status" && <div>{capitalize(props.pfe[key])}</div>}
                  {key === "approval_date" && <div>{props.pfe[key] === null ? "Not Approved" : props.pfe[key]}</div>}
                  {key !== "status" && key !== "approval_date" && <div>{props.pfe[key]}</div>}
                  {key === "star_end_date" && <div>{props.pfe["start_date"]} – {props.pfe["end_date"]}</div>}
                </div>
              ))}

              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column' }}>
                {props.pfe.type === "TS" && <IconButton label='Overview' icon='eye' basic color='blue' onClick={() => setIsOverviewModalOpen(true)} />}
                {props.pfe.type === "TS" && <IconButton label='Financial Data' icon='chart bar' color='green' onClick={() => props.setFinancialDataModalOpen(true)} />}
                {props.pfe.type === "TS" && <IconButton label='Save Draft' icon='hdd outline' color='blue' onClick={() => onSave(false)} disabled={isButtonDisabled()} />}
                <IconButton label='Confirm PFE' icon='check circle outline' color='teal' onClick={openConfirmModal} disabled={isButtonDisabled()} />
              </div>
            </Segment>

            {props.pfe.type === "PROD" && <PfeNextEditProductContainer pfe_id={pfe_id} />}
            {props.pfe.type === "TS" && <PfeNextEditServiceContainer pfe_id={pfe_id} overview={isOverviewModalOpen} setOverview={setIsOverviewModalOpen} />}

            {/* ADA-107: nuova modale di conferma */}
            <Modal
              open={isConfirmModalOpen}
              onClose={handleCloseModal}
              size='small'
            >
              <Modal.Header>Confirm PFE</Modal.Header>
              <Modal.Content>
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 'bold' }}>
                      Note (Optional)
                    </label>
                    <span style={{ fontSize: '0.875rem', color: confirmNote.length === MAX_NOTE_LENGTH ? '#db2828' : '#666' }}>
                      {confirmNote.length}/{MAX_NOTE_LENGTH}
                    </span>
                  </div>
                  <TextArea
                    placeholder='Add a note that will be included in the confirmation email...'
                    value={confirmNote}
                    onChange={handleNoteChange}
                    onPaste={handleNotePaste}
                    rows={3}
                    style={{ width: '100%' }}
                  />
                  {noteError && (
                    <Message negative size='tiny' style={{ marginTop: '0.5rem' }}>
                      {noteError}
                    </Message>
                  )}
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.25rem' }}>
                    YUBIK Offer Code
                  </label>
                  <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.5rem' }}>
                    Format: T_CCC_YYYY_AAAA or T_CCC_YYYY_AAAA_VVV (e.g., O_ITA_2025_A001 or O_ESP_2025_A001_V01)
                  </p>
                  <Input
                    fluid
                    placeholder='Enter YUBIK code (e.g., O_ITA_2025_A001)...'
                    value={currentYubikCode}
                    onChange={(e) => {
                      setCurrentYubikCode(e.target.value.toUpperCase());
                      setYubikCodeError('');
                    }}
                    onKeyPress={handleKeyPress}
                    error={yubikCodeError ? true : false}
                    disabled={isYubikNotAvailable}
                    action={{
                      icon: 'plus',
                      content: 'Add',
                      onClick: addYubikCode,
                      disabled: isYubikNotAvailable
                    }}
                  />
                  {yubikCodeError && (
                    <Message negative size='tiny' style={{ marginTop: '0.5rem' }}>
                      {yubikCodeError}
                    </Message>
                  )}

                  <div style={{ marginTop: '1rem' }}>
                    <Checkbox
                      disabled={newYubikCodes.length > 0}
                      checked={isYubikNotAvailable}
                      onChange={(e, data) => setIsYubikNotAvailable(data.checked)}
                      label="Offer code is not available and the project has been approved by director"
                    />
                  </div>

                  {yubikValidationError && (
                    <Message negative size='tiny' style={{ marginTop: '0.5rem' }}>
                      {yubikValidationError}
                    </Message>
                  )}
                </div>

                {(existingYubikCodes.length > 0 || newYubikCodes.length > 0) && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                      YUBIK Offer Codes
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {/* Codici esistenti - non modificabili */}
                      {existingYubikCodes.map((code, index) => (
                        <Label key={`existing-${index}`} color='grey' size='large'>
                          {code}
                        </Label>
                      ))}

                      {/* Nuovi codici - eliminabili */}
                      {newYubikCodes.map((code, index) => (
                        <Label key={`new-${index}`} color='teal' size='large'>
                          {code}
                          <Label.Detail>
                            <span
                              style={{ cursor: 'pointer', marginLeft: '0.25rem' }}
                              onClick={() => removeYubikCode(code)}
                            >
                              x
                            </span>
                          </Label.Detail>
                        </Label>
                      ))}
                    </div>
                  </div>
                )}
              </Modal.Content>
              <Modal.Actions style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  color='red'
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  color='teal'
                  icon='check circle outline'
                  content='Confirm'
                  onClick={() => {
                    // Validazione YUBIK obbligatorio
                    if (newYubikCodes.length === 0 && !isYubikNotAvailable) {
                      setYubikValidationError('Please add at least one YUBIK Offer Code or check the declaration below');
                      return;
                    }

                    setYubikValidationError('');
                    setIsConfirmModalOpen(false);
                    // Determina array da inviare al backend
                    const codesToSend = isYubikNotAvailable ? [] : newYubikCodes;
                    onSave(true, confirmNote, codesToSend);
                  }}
                />
              </Modal.Actions>
            </Modal>
            {/* ADA-107: nuova modale di conferma */}
          </>
        }
      </Can>
    </div>
  )
}


const mapStateToProps = state => ({
  loading: state.professionalFiguresReducer.loadingPfe ||
    state.cutoffReducer.loading_pm,
  pfe: state.professionalFiguresReducer.pfe,
  pfeDetails: state.professionalFiguresReducer.pfeDetails,

  costsAndRevenues: state.professionalFiguresReducer.costsAndRevenues,
  professionalFigures: state.professionalFiguresReducer.pfeProfessionalFigures,
  financialData: state.professionalFiguresReducer.financialData,
});

const mapDispatchToProps = dispatch => {
  return {
    getPfe: (pfeId) => dispatch(startGetPfe(pfeId)),
    startSavePfe: (pfeId, body, onFailure, onSuccess) => dispatch(startSavePfe(pfeId, body, onFailure, onSuccess)),
    getCutoffPM: () => dispatch(startGetCutoffPM()),
    setFinancialDataModalOpen: (isOpen) => dispatch(setFinancialDataModalOpen(isOpen)),
    clearPfe: () => dispatch(clearPfe())
  }
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PfeNextEditContainer);
